/**
 * «Есть новое» для отложенных ответов (OG-2b).
 *
 * Сервер дозаписывает ответ прямо в сессию и помечает сообщение
 * `deferred_run_id`, но флага прочитанности у него нет и заводить его не надо:
 * «видел» — свойство браузера, а не заказа. Держим его в localStorage по ключу
 * `seen-deferred:<sessionId>`, значок гаснет при открытии сессии.
 *
 * Лист без импортов из фич — им пользуются и список сессий, и заказ из Разговора.
 */

const SEEN_KEY_PREFIX = 'seen-deferred:';

/** Доставленные отложенные ответы: sessionId -> id сообщений. */
const delivered = new Map<string, Set<string>>();

function readSeen(sessionId: string): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY_PREFIX + sessionId);
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSeen(sessionId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(SEEN_KEY_PREFIX + sessionId, JSON.stringify([...ids]));
  } catch {
    /* приватный режим / переполненное хранилище — значок просто не погаснет */
  }
}

export function registerDeferredDeliveries(sessionId: string, messageIds: Iterable<string>): void {
  if (!sessionId) return;
  const known = delivered.get(sessionId) ?? new Set<string>();
  for (const id of messageIds) {
    if (id) known.add(id);
  }
  delivered.set(sessionId, known);
}

export function hasUnreadDeferred(sessionId: string): boolean {
  const known = delivered.get(sessionId);
  if (!known?.size) return false;
  const seen = readSeen(sessionId);
  return [...known].some((id) => !seen.has(id));
}

/** Сессию открыли — всё доставленное в ней считается увиденным. */
export function markDeferredSeen(sessionId: string, messageIds: Iterable<string> = []): void {
  registerDeferredDeliveries(sessionId, messageIds);
  const known = delivered.get(sessionId);
  if (!known?.size) return;
  const seen = readSeen(sessionId);
  known.forEach((id) => seen.add(id));
  writeSeen(sessionId, seen);
}

export function forgetDeferredDeliveries(): void {
  delivered.clear();
}
